import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from '@/contexts/ModalContext';
import {
  ConfirmationDialog,
  ConfirmationDialogProps,
} from '~/global/components/confirmation-dialog/ConfirmationDialog';

export interface ConfirmationOptions {
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
}

type Props = ConfirmationOptions;

export function useConfirmation(
  onConfirm: () => unknown,
  props?: Props,
): {
  dispatchConfirmationDialog(): void;
};
export function useConfirmation<P1>(
  onConfirm: (p1: P1) => unknown,
  props?: Props,
): {
  dispatchConfirmationDialog(p1: P1): void;
};
export function useConfirmation<P1, P2>(
  onConfirm: (p1: P1, p2: P2) => unknown,
  props?: Props,
): {
  dispatchConfirmationDialog(p1: P1, p2: P2): void;
};
export function useConfirmation(
  onConfirm: (...args: unknown[]) => unknown,
  props?: Props,
) {
  const { showModal } = useModal();
  const { t } = useTranslation('common');

  const dispatchConfirmationDialog = useCallback(
    (...args: unknown[]) => {
      // Use common strings as defaults if no props provided
      const options: ConfirmationOptions = {
        title: props?.title || t('confirmation.title'),
        description: props?.description || t('confirmation.description'),
        confirmText: props?.confirmText || t('confirmation.confirm'),
        cancelText: props?.cancelText || t('confirmation.cancel'),
        ...props, // Allow overriding with custom values
      };

      showModal(ConfirmationDialog, {
        onConfirm: () => onConfirm(...args),
        options,
      });
    },
    [onConfirm, props, showModal, t],
  );

  return {
    dispatchConfirmationDialog,
  };
}
